import { DropDownChevronDownIcon } from 'assets/icons';
import { Popover } from '@headlessui/react';
import React, { FC, useState } from 'react';
import { ServiceAreaInfo } from 'containers/jobs/jobs.model';
import { metersToRoundedMiles } from 'utils/commonUtils';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import ServiceAreaCreateModal from './ServiceAreaCreateModal';
import { CreateServiceAreaFormData } from 'containers/settings/service-areas/serviceAreas.model';
import { selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface ServiceAreaDropdownProps {
  serviceAreas: ServiceAreaInfo[];
  selectedArea: ServiceAreaInfo | null;
  onSelectArea: (area: ServiceAreaInfo) => void;
  isLoading: boolean;
  onCreateServiceArea?: (data: CreateServiceAreaFormData) => void;
  isLoadingCreateServiceArea?: boolean;
  isLatLngZero?: boolean;
}

const ServiceAreaDropdown: FC<ServiceAreaDropdownProps> = ({
  serviceAreas,
  selectedArea,
  onSelectArea,
  isLoading,
  onCreateServiceArea,
  isLoadingCreateServiceArea,
  isLatLngZero
}) => {
  const { editJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveServiceArea = (data: CreateServiceAreaFormData) => {
    if (onCreateServiceArea) {
      onCreateServiceArea(data);
    }
  };

  const anchorComponent = () => (
    <div
      tabIndex={0}
      aria-label="Choose service area"
      className="flex w-full cursor-pointer items-center justify-between rounded bg-secondaryBtn px-5 py-4 outline-none"
      role="button">
      <span
        className={`text-17px text-primaryText ${selectedArea ? 'text-primaryText' : 'text-accentText opacity-70'}`}>
        {selectedArea?.serviceAreaName || ''}
        {selectedArea?.distance &&
          !isLatLngZero &&
          ` (${
            selectedArea?.isEnclosing
              ? `within area, ${metersToRoundedMiles(selectedArea.distance)} miles away from center`
              : `${metersToRoundedMiles(selectedArea.distance)} miles away`
          })`}
      </span>
      <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
    </div>
  );

  return (
    <div className="w-full">
      <Popover className="relative">
        {({ close }) => (
          <>
            <Popover.Button disabled={isLoading} className="w-full focus:outline-none">
              {anchorComponent()}
            </Popover.Button>

            <Popover.Panel className="absolute z-10 mt-2 w-full min-w-[320px] rounded-md bg-white shadow-lg">
              <div className="max-h-[400px] overflow-y-auto py-2">
                {editJobSettings && (
                  <CustomButton
                    className="w-full justify-start px-4 py-2"
                    type={ButtonType.GHOST}
                    childrenContainerStyles="text-17px"
                    onClick={() => {
                      close();
                      handleOpenModal();
                    }}
                    id="create-service-area-button">
                    + Create service area
                  </CustomButton>
                )}
                {serviceAreas?.map(area => (
                  <div
                    key={area.serviceAreaId}
                    tabIndex={0}
                    aria-label={`Select ${area.serviceAreaName}`}
                    className={`
                    cursor-pointer px-4 py-3 text-17px text-primaryText
                    outline-none hover:bg-secondaryBtn
                    ${
                      selectedArea?.serviceAreaId === area.serviceAreaId
                        ? 'font-semibold text-primaryText'
                        : 'font-medium'
                    }
                  `}
                    onClick={() => {
                      onSelectArea(area);
                      close();
                    }}
                    role="option"
                    aria-selected={selectedArea?.serviceAreaId === area.serviceAreaId}>
                    {area.serviceAreaName}
                    {area.distance &&
                      !isLatLngZero &&
                      ` (${
                        area.isEnclosing
                          ? `within area, ${metersToRoundedMiles(area.distance)} miles away from center`
                          : `${metersToRoundedMiles(area.distance)} miles away`
                      })`}
                  </div>
                ))}
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>

      <ServiceAreaCreateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveServiceArea}
        isLoading={isLoadingCreateServiceArea}
      />
    </div>
  );
};

export default ServiceAreaDropdown;
